import React, { useState, useEffect, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Select,
  Checkbox,
  Tooltip as AntdTooltip,
  Typography,
  Row,
  Col,
  Divider,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const QMax = ({ streetName, data }) => {
  const options = ["1min", "5min", "10min", "15min", "30min"];
  const defaultSelectedOption = "10min";
  const [ticks, setTicks] = useState({});
  const [maxQmax, setMaxQmax] = useState(null);
  const [selectedOption, setSelectedOption] = useState(defaultSelectedOption);
  const [extractedgraphData, setExtractedgraphData] = useState([]);
  const [vehicleVisibility, setVehicleVisibility] = useState({});

  const colors = useMemo(
    () => [
      "#8884d8",
      "#82ca9d",
      "#ffc658",
      "#ff7300",
      "#ff84f0",
      "#0088FE",
      "#00C49F",
      "#FFBB28",
      "#FF8042",
      "#af111c",
      "#c02e17",
    ],
    []
  );

  // Helper to normalize IDs (handles "455.0" vs 455 format)
  const normalizeId = (id) => {
    if (id === null || id === undefined) return null;
    const parsed = typeof id === 'string' ? parseFloat(id) : id;
    return Number.isInteger(parsed) ? parsed : Math.floor(parsed);
  };

  useEffect(() => {
    if (streetName) {
      const normalizedStreetName = normalizeId(streetName);
      const streetData = data.find((street) => normalizeId(street.id) === normalizedStreetName);
      if (streetData && streetData.indicatoriGlobali) {
        setMaxQmax(streetData.indicatoriGlobali.maxQmax);
      }

      if (
        streetData &&
        streetData.serieTemporale &&
        streetData.serieTemporale.qmax &&
        streetData.serieTemporale.qmax[selectedOption]
      ) {
        const graphData = streetData.serieTemporale.qmax[selectedOption];
        const newData = Object.keys(graphData).map((vehicleType, index) => ({
          name: vehicleType,
          data: Object.entries(graphData[vehicleType]).map(([key, value]) => ({
            x: parseInt(key),
            y: value,
          })),
          color: colors[index % colors.length],
        }));
        setExtractedgraphData(newData);
        const initialVisibilityState = newData.reduce((acc, vehicle) => {
          acc[vehicle.name] = true;
          return acc;
        }, {});
        setVehicleVisibility(initialVisibilityState);
      }

      const newTicks = {
        "1min": [10, 20, 30, 40, 50, 60],
        "5min": [10, 20, 30, 40, 50, 60],
        "10min": [10, 20, 30, 40, 50, 60],
        "15min": [15, 30, 45, 60],
        "30min": [30, 40, 50, 60],
      };
      setTicks(newTicks);
    }
  }, [streetName, selectedOption, data, colors]);

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setVehicleVisibility((prevVisibility) => ({
      ...prevVisibility,
      [name]: checked,
    }));
  };

  return (
    <div style={{ padding: "16px" }}>
      {/* Header */}
      <Row align="middle" gutter={8}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Coda Max
          </Title>
        </Col>
        <Col>
          <AntdTooltip
            color="#768079"
            title="Massimo numero di veicoli accodati nell'intervallo di tempo. 
La durata degli intervalli può essere selezionata attraverso l'apposito menu a tendina. 
La coda è definita come un insieme contiguo di veicoli che sono fermi o si muovono a velocità molto ridotte."
          >
            <InfoCircleOutlined style={{ fontSize: 18, color: "#555" }} />
          </AntdTooltip>
        </Col>
      </Row>

      <Divider />

      {/* Chart + Filters */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={18}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart margin={{ top: 5, right: 30, left: 20, bottom: 25 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="x"
                type="number"
                ticks={ticks[selectedOption]}
                domain={["dataMin", "dataMax"]}
                allowDataOverflow={true}
                label={{
                  value: "Tempo",
                  position: "insideBottom",
                  offset: -10,
                }}
              />
              <YAxis
                label={{
                  value: "Veicoli",
                  angle: -90,
                  position: "insideLeft",
                }}
              />
              <Tooltip />
              {extractedgraphData.map(
                (vehicle) =>
                  vehicleVisibility[vehicle.name] && (
                    <Line
                      key={vehicle.name}
                      type="monotone"
                      dataKey="y"
                      data={vehicle.data}
                      name={vehicle.name}
                      stroke={vehicle.color}
                    />
                  )
              )}
            </LineChart>
          </ResponsiveContainer>
        </Col>

        {/* Checkboxes + Value */}
        <Col xs={24} md={6}>
          <div style={{ marginBottom: 16 }}>
            {extractedgraphData.map((vehicle) => (
              <div key={vehicle.name}>
                <Checkbox
                  checked={vehicleVisibility[vehicle.name]}
                  onChange={handleCheckboxChange}
                  name={vehicle.name}
                  style={{ color: vehicle.color }}
                >
                  <Text style={{ color: vehicle.color }}>{vehicle.name}</Text>
                </Checkbox>
              </div>
            ))}
          </div>

          <Divider />

          <Title level={5}>{`Coda max: ${maxQmax ?? 0}`}</Title>
        </Col>
      </Row>

      {/* Select */}
      <Row justify="center" style={{ marginTop: 24 }}>
        <Select
          value={selectedOption}
          onChange={setSelectedOption}
          options={options.map((o) => ({ value: o, label: o }))}
          style={{ width: 160 }}
        />
      </Row>
    </div>
  );
};

export default QMax;
